import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, AlertTriangle, TrendingUp, DollarSign, Shield,
  ShoppingCart, Apple, Plane, Trophy, Hotel,
  Clock, Eye, Zap, CheckCircle, XCircle,
  Calculator, FileText, Download, ExternalLink, Activity
} from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
import TabbedSection from '../components/TabbedSection';
import AccordionSection from '../components/AccordionSection';
import DeploymentEstimator from '../components/DeploymentEstimator';

/* ─── Reusable: Enterprise Horizontal Bar Chart ─── */
const accentColorMap = {
  sky: 'bg-[#0077FF]',
  amber: 'bg-amber-500',
  emerald: 'bg-emerald-500',
};

const ComparisonChart = ({ data, maxValue, accentColor = 'sky', contextLine, attributionLine }) => (
  <div className="space-y-4">
    {data.map((item, i) => {
      const pct = Math.max(4, (item.value / maxValue) * 100);
      return (
        <div key={i} data-testid={`chart-bar-${i}`}>
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">{item.label}</span>
            <span className="text-sm font-bold text-white font-mono">{item.display}</span>
          </div>
          <div className="w-full h-2.5 bg-slate-700/60 rounded-full overflow-hidden relative">
            <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'repeating-linear-gradient(90deg, transparent, transparent 19%, rgba(255,255,255,0.15) 19%, rgba(255,255,255,0.15) 20%)' }} />
            <div
              className={`h-full rounded-full transition-all duration-700 ease-out ${accentColorMap[accentColor] || 'bg-[#0077FF]'}`}
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>
      );
    })}
    {contextLine && <p className="text-[10px] text-slate-500 leading-relaxed mt-3 italic" data-testid="chart-context-line">{contextLine}</p>}
    {attributionLine && <p className="text-[10px] text-slate-600 mt-1" data-testid="chart-attribution">{attributionLine}</p>}
  </div>
);

/* ─── Reusable: Stat Panel ─── */
const StatPanel = ({ label, value, sub }) => (
  <div className="text-center">
    <p className="text-[10px] text-slate-500 uppercase tracking-widest mb-1">{label}</p>
    <p className="text-xl md:text-2xl font-bold text-white font-mono leading-none">{value}</p>
    {sub && <p className="text-[10px] text-slate-500 mt-1">{sub}</p>}
  </div>
);

/* ─── Reusable: Flow Step (for risk insight diagrams) ─── */
const FlowDiagram = ({ title, steps, color = 'red', icon: Icon }) => {
  const isRed = color === 'red';
  return (
    <div className="bg-slate-800/80 rounded-2xl p-6 border border-slate-700" data-testid={`flow-${color}`}>
      <div className="flex items-center gap-2.5 mb-6">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${isRed ? 'bg-red-500/20' : 'bg-[#0077FF]/20'}`}>
          <Icon className={`w-5 h-5 ${isRed ? 'text-red-400' : 'text-[#4DB8FF]'}`} />
        </div>
        <h4 className="text-base font-bold text-white">{title}</h4>
      </div>
      <div className="space-y-0">
        {steps.map((step, i, arr) => (
          <div key={i}>
            <div className="flex items-center gap-3">
              <div className={`w-7 h-7 border rounded-full flex items-center justify-center flex-shrink-0 ${
                isRed ? 'border-red-500/40 bg-red-500/10' : 'border-[#0077FF]/40 bg-[#0077FF]/10'
              }`}>
                <span className={`text-[10px] font-bold ${isRed ? 'text-red-400' : 'text-[#4DB8FF]'}`}>{i + 1}</span>
              </div>
              <span className="text-sm text-slate-300 font-medium">{step}</span>
            </div>
            {i < arr.length - 1 && (
              <div className={`ml-3.5 border-l h-4 ${isRed ? 'border-red-500/30' : 'border-[#0077FF]/30'}`} />
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const OperationalRiskPage = () => {
  const { t, language } = useLanguage();
  const getLocalizedPath = (path) => `/${language}${path}`;
  const calculatorRef = useRef(null);

  // Exposure & Loss Calculator state
  const [calcIndustry, setCalcIndustry] = useState('');
  const [calcSize, setCalcSize] = useState('');
  const [calcVisitors, setCalcVisitors] = useState('');
  const [calcLocations, setCalcLocations] = useState('');
  const [calcResults, setCalcResults] = useState(null);


  const scrollToCalculator = () => {
    calculatorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const calculateExposure = () => {
    const size = parseInt(calcSize) || 50000;
    const visitors = parseInt(calcVisitors) || 500;
    const locations = parseInt(calcLocations) || 1;
    const incidentRate = size > 100000 ? 0.08 : size > 50000 ? 0.06 : 0.04;
    const incidents = Math.round(visitors * 365 * incidentRate / 1000 * locations);
    const avgClaim = 35000;
    const claimRate = 0.12;
    const liability = Math.round(incidents * claimRate * avgClaim);
    const disruption = Math.round(incidents * 2.5 * 150);
    setCalcResults({ incidents, liability, disruption });
  };

  // Chart comparison data (used in probability model right panels)
  const comparisonData = [
    { label: 'Large Retail Warehouse Facility', value: 0.15, display: '$80K–$200K' },
    { label: 'Major Sports Stadium', value: 1.5, display: '$500K–$3M' },
    { label: 'Large International Airport Terminal', value: 10, display: '$10M+' },
  ];

  const statIcons = [AlertTriangle, DollarSign, TrendingUp, Shield];
  const statColors = ['text-red-400 bg-red-500/20', 'text-amber-400 bg-amber-500/20', 'text-orange-400 bg-orange-500/20', 'text-rose-400 bg-rose-500/20'];
  const industryIcons = [ShoppingCart, Apple, Plane, Trophy, Hotel];
  const industryBorders = ['border-red-200', 'border-emerald-200', 'border-[#0077FF]/20', 'border-amber-200', 'border-violet-200'];
  const industryIconBgs = ['bg-red-100 text-red-600', 'bg-emerald-100 text-emerald-600', 'bg-[#0077FF]/10 text-[#0077FF]', 'bg-amber-100 text-amber-600', 'bg-violet-100 text-violet-600'];

  return (
    <div className="min-h-screen" data-testid="operational-risk-page">

      {/* ═══════════ 1. HERO ═══════════ */}
      <section className="relative pt-32 md:pt-40 pb-16 md:pb-24">
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-20 right-0 w-[500px] h-[500px] bg-gradient-to-br from-red-50 to-amber-50 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-gradient-to-tr from-slate-100 to-red-50 rounded-full blur-3xl" />
        </div>
        <div className="container-max px-6 md:px-12 lg:px-24 relative">
          <div className="text-center max-w-4xl mx-auto">
            <span className="text-caption text-[#0077FF] mb-4 block" data-testid="risk-label">
              {t('operationalRisk.hero.label')}
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-[56px] font-bold text-slate-900 leading-tight tracking-tight mb-6" data-testid="risk-title">
              {t('operationalRisk.hero.title')}
            </h1>
            <p className="text-lg md:text-xl text-slate-600 leading-relaxed mb-6">
              {t('operationalRisk.hero.subtitle')}
            </p>
            <p className="text-base text-slate-500 leading-relaxed max-w-3xl mx-auto mb-10">
              {t('operationalRisk.hero.intro')}
            </p>
            <div className="flex flex-col items-center justify-center gap-3">
              <button onClick={scrollToCalculator} className="btn-primary bg-[#0077FF] hover:bg-[#0066DD] text-white text-base w-full sm:w-auto" data-testid="hero-estimator-btn">
                <Calculator className="w-5 h-5" />
                {t('operationalRisk.deploymentEstimator.scrollBtn')}
              </button>
              <button className="btn-primary bg-[#0077FF] hover:bg-[#0066DD] text-white text-base w-full sm:w-auto" data-testid="hero-risk-model-btn" onClick={() => {
                const el = document.getElementById('estimator-section');
                if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }}>
                <Shield className="w-5 h-5" />
                Model Risk Coverage
              </button>
              <button className="btn-primary bg-[#0077FF] hover:bg-[#0066DD] text-white text-base w-full sm:w-auto" data-testid="hero-exposure-btn" onClick={() => {
                const el = document.getElementById('exposure');
                if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }}>
                <DollarSign className="w-5 h-5" />
                {t('operationalRisk.hero.calculateExposureBtn') || 'Calculate Exposure'}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════ 2. INDUSTRY STATISTICS ═══════════ */}
      <section className="py-20 bg-slate-900" data-testid="risk-stats-section">
        <div className="container-max px-6 md:px-12 lg:px-24">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <span className="text-caption text-[#4DB8FF] mb-4 block">{t('operationalRisk.stats.label')}</span>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">{t('operationalRisk.stats.title')}</h2>
            <p className="text-lg text-slate-400">{t('operationalRisk.stats.subtitle')}</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {(t('operationalRisk.stats.items') || []).map((stat, i) => {
              const Ic = statIcons[i];
              const [textColor, bgColor] = statColors[i].split(' ');
              return (
                <div key={i} className="bg-slate-800/60 rounded-2xl p-8 border border-slate-700 text-center flex flex-col" data-testid={`stat-card-${i}`}>
                  <div className={`w-14 h-14 mx-auto mb-5 rounded-xl flex items-center justify-center ${bgColor}`}>
                    <Ic className={`w-7 h-7 ${textColor}`} />
                  </div>
                  <p className="text-3xl md:text-4xl font-bold text-white mb-2 font-mono">{stat.value}</p>
                  <p className="text-[#4DB8FF] font-semibold text-sm mb-3">{stat.label}</p>
                  <p className="text-slate-400 text-sm leading-relaxed mb-4 flex-grow">{stat.description}</p>
                  {stat.source && (
                    <p className="text-slate-500 text-xs italic border-t border-slate-700 pt-3 mt-auto" data-testid={`stat-source-${i}`}>
                      {stat.source}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ═══════════ 3. HAZARD DISTRIBUTION ANALYSIS ═══════════ */}
      <section className="section-padding bg-white" data-testid="hazard-distribution-section">
        <div className="container-max px-6 md:px-12 lg:px-24">
          <div className="max-w-5xl mx-auto">
            <div className="text-center max-w-3xl mx-auto mb-14">
              <span className="text-caption text-[#0077FF] mb-4 block">{t('operationalRisk.hazardDistribution.label')}</span>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">{t('operationalRisk.hazardDistribution.title')}</h2>
              <p className="text-lg text-slate-600">{t('operationalRisk.hazardDistribution.subtitle')}</p>
            </div>
            <div className="grid lg:grid-cols-5 gap-10 items-start">
              {/* Narrative — 3 cols */}
              <div className="lg:col-span-3 space-y-5 text-slate-600 leading-relaxed">
                <p>{t('operationalRisk.hazardDistribution.intro')}</p>
                <p>{t('operationalRisk.hazardDistribution.body')}</p>
                <p className="font-medium text-slate-700">{t('operationalRisk.hazardDistribution.conclusion')}</p>
              </div>
              {/* Hazard Types — 2 cols */}
              <div className="lg:col-span-2">
                <div className="bg-slate-50 rounded-2xl border border-slate-200 p-6">
                  <h4 className="text-sm font-bold text-slate-800 uppercase tracking-wider mb-4">{t('operationalRisk.hazardDistribution.hazardTypesLabel')}</h4>
                  <ul className="space-y-3">
                    {(t('operationalRisk.hazardDistribution.hazardTypes') || []).map((type, i) => (
                      <li key={i} className="flex items-start gap-2.5 text-sm text-slate-700">
                        <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                        <span>{type}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Reinforcement: After Distribution */}
      <div className="py-8 bg-white" data-testid="reinforcement-distribution">
        <div className="container-max px-6 md:px-12 lg:px-24">
          <div className="max-w-3xl mx-auto bg-[#0077FF]/5 rounded-xl border border-[#0077FF]/20 p-6 flex items-start gap-4">
            <div className="w-8 h-8 bg-[#0077FF]/10 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
              <Zap className="w-4 h-4 text-[#0077FF]" />
            </div>
            <div>
              <h4 className="text-sm font-bold text-slate-900 mb-1">{t('operationalRisk.reinforcement.afterDistribution.title')}</h4>
              <p className="text-sm text-slate-600 leading-relaxed">{t('operationalRisk.reinforcement.afterDistribution.body')}</p>
            </div>
          </div>
        </div>
      </div>

      {/* ═══════════ 4. HIGH TRAFFIC & HAZARD PROBABILITY ═══════════ */}
      <section className="section-padding bg-slate-50" data-testid="high-traffic-section">
        <div className="container-max px-6 md:px-12 lg:px-24">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <span className="text-caption text-[#0077FF] mb-4 block">{t('operationalRisk.highTraffic.label')}</span>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">{t('operationalRisk.highTraffic.title')}</h2>
              <p className="text-lg text-slate-600">{t('operationalRisk.highTraffic.subtitle')}</p>
            </div>
            <div className="space-y-5 text-slate-600 leading-relaxed text-center max-w-3xl mx-auto">
              <p>{t('operationalRisk.highTraffic.body1')}</p>
              <p>{t('operationalRisk.highTraffic.body2')}</p>
              <p className="text-xl font-bold text-slate-900 py-2">{t('operationalRisk.highTraffic.opportunity')}</p>
              <p>{t('operationalRisk.highTraffic.body3')}</p>
              <p>{t('operationalRisk.highTraffic.body4')}</p>
              <p className="font-medium text-slate-800">{t('operationalRisk.highTraffic.conclusion')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Reinforcement: After Traffic */}
      <div className="py-8 bg-slate-50" data-testid="reinforcement-traffic">
        <div className="container-max px-6 md:px-12 lg:px-24">
          <div className="max-w-3xl mx-auto bg-[#0077FF]/5 rounded-xl border border-[#0077FF]/20 p-6 flex items-start gap-4">
            <div className="w-8 h-8 bg-[#0077FF]/10 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
              <Eye className="w-4 h-4 text-[#0077FF]" />
            </div>
            <div>
              <h4 className="text-sm font-bold text-slate-900 mb-1">{t('operationalRisk.reinforcement.afterTraffic.title')}</h4>
              <p className="text-sm text-slate-600 leading-relaxed">{t('operationalRisk.reinforcement.afterTraffic.body')}</p>
            </div>
          </div>
        </div>
      </div>

      {/* ═══════════ 5. WHERE HAZARDS OCCUR ═══════════ */}
      <section className="section-padding bg-white" data-testid="hazard-locations-section">
        <div className="container-max px-6 md:px-12 lg:px-24">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <span className="text-caption text-[#0077FF] mb-4 block">{t('operationalRisk.hazardLocations.label')}</span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">{t('operationalRisk.hazardLocations.title')}</h2>
            <p className="text-lg text-slate-600">{t('operationalRisk.hazardLocations.subtitle')}</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-5">
            {(t('operationalRisk.hazardLocations.industries') || []).map((ind, i) => {
              const Ic = industryIcons[i];
              return (
                <div key={i} className={`bg-slate-50 rounded-2xl p-6 border ${industryBorders[i]} hover:shadow-md transition-shadow`} data-testid={`hazard-card-${i}`}>
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${industryIconBgs[i]}`}>
                    <Ic className="w-6 h-6" />
                  </div>
                  <h3 className="text-base font-bold text-slate-900 mb-3">{ind.title}</h3>
                  <ul className="space-y-1.5">
                    {(ind.hazards || []).map((h, j) => (
                      <li key={j} className="flex items-start gap-2 text-sm text-slate-600">
                        <AlertTriangle className="w-3.5 h-3.5 text-amber-500 mt-0.5 flex-shrink-0" />
                        <span>{h}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ═══════════ 5b. WHERE HAZARDS OCCUR INSIDE FACILITIES ═══════════ */}
      <section className="py-20 bg-slate-900" data-testid="facility-hazards-section">
        <div className="container-max px-6 md:px-12 lg:px-24">
          {/* Header */}
          <div className="text-center max-w-3xl mx-auto mb-6">
            <span className="text-caption text-[#4DB8FF] mb-4 block">{t('operationalRisk.facilityHazards.label')}</span>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">{t('operationalRisk.facilityHazards.title')}</h2>
            <p className="text-lg text-slate-400 mb-5">{t('operationalRisk.facilityHazards.subtitle')}</p>
            <p className="text-sm text-slate-500 font-medium italic">{t('operationalRisk.facilityHazards.statistic')}</p>
          </div>
          <div className="text-center max-w-3xl mx-auto mb-16 space-y-3">
            <p className="text-sm text-slate-400 leading-relaxed">{t('operationalRisk.facilityHazards.intro1')}</p>
            <p className="text-sm text-slate-400 leading-relaxed">{t('operationalRisk.facilityHazards.intro2')}</p>
            <p className="text-sm text-slate-400 leading-relaxed">{t('operationalRisk.facilityHazards.intro3')}</p>
          </div>

          {/* Facility Hazard Tabs: Retail / Stadium / Airport */}
          <TabbedSection
            testId="facility-hazards-tabs"
            tabs={[
              {
                id: 'retail',
                label: t('operationalRisk.facilityHazards.tabs.retail') || 'Retail',
                content: (
                  <div data-testid="facility-retail">
                    <div className="bg-slate-800/60 rounded-2xl border border-slate-700 overflow-hidden">
                      <div className="px-6 py-4 border-b border-slate-700 flex items-center gap-3">
                        <ShoppingCart className="w-5 h-5 text-[#4DB8FF]" />
                        <h3 className="text-lg font-bold text-white">{t('operationalRisk.facilityHazards.retail.title')}</h3>
                      </div>
                      <div className="p-6 md:p-8">
                        <div className="flex flex-col lg:flex-row gap-8">
                          <div className="flex-1">
                            <svg viewBox="0 0 600 380" className="w-full h-auto" data-testid="retail-diagram">
                              <defs>
                                <pattern id="retailGrid" width="30" height="30" patternUnits="userSpaceOnUse">
                                  <path d="M 30 0 L 0 0 0 30" fill="none" stroke="rgba(148,163,184,0.06)" strokeWidth="0.5"/>
                                </pattern>
                                <pattern id="retailGridFine" width="10" height="10" patternUnits="userSpaceOnUse">
                                  <path d="M 10 0 L 0 0 0 10" fill="none" stroke="rgba(148,163,184,0.025)" strokeWidth="0.3"/>
                                </pattern>
                              </defs>
                              <rect width="600" height="380" fill="#1e293b" rx="8"/>
                              <rect width="600" height="380" fill="url(#retailGridFine)" rx="8"/>
                              <rect width="600" height="380" fill="url(#retailGrid)" rx="8"/>
                              {[60,120,180,240,300,360,420,480,540].map(x => <line key={`rt${x}`} x1={x} y1="0" x2={x} y2="4" stroke="rgba(148,163,184,0.12)" strokeWidth="0.5"/>)}
                              {[60,120,180,240,300,360].map(y => <line key={`rl${y}`} x1="0" y1={y} x2="4" y2={y} stroke="rgba(148,163,184,0.12)" strokeWidth="0.5"/>)}
                              <rect x="40" y="30" width="520" height="320" fill="none" stroke="rgba(148,163,184,0.15)" strokeWidth="0.75" rx="4"/>
                              <rect x="40" y="310" width="80" height="40" fill="rgba(148,163,184,0.04)" stroke="rgba(148,163,184,0.1)" strokeWidth="0.5" rx="2"/>
                              <text x="80" y="335" fill="rgba(148,163,184,0.8)" fontSize="8" textAnchor="middle" fontFamily="system-ui">{t('operationalRisk.facilityHazards.retail.zones.entrance')}</text>
                              <rect x="480" y="310" width="80" height="40" fill="rgba(148,163,184,0.04)" stroke="rgba(148,163,184,0.1)" strokeWidth="0.5" rx="2"/>
                              <text x="520" y="335" fill="rgba(148,163,184,0.8)" fontSize="8" textAnchor="middle" fontFamily="system-ui">{t('operationalRisk.facilityHazards.retail.zones.exit')}</text>
                              <rect x="60" y="50" width="110" height="180" fill="rgba(239,68,68,0.65)" stroke="rgba(239,68,68,0.85)" strokeWidth="0.5" rx="3"/>
                              {[60,82,104,126,148,170,192].map(y => <rect key={`ga${y}`} x="70" y={y} width="90" height="14" fill="rgba(239,68,68,0.35)" rx="1.5"/>)}
                              <text x="115" y="222" fill="rgba(226,232,240,1.0)" fontSize="8" textAnchor="middle" fontFamily="system-ui">{t('operationalRisk.facilityHazards.retail.zones.grocery')}</text>
                              <rect x="190" y="50" width="100" height="180" fill="rgba(239,68,68,0.65)" stroke="rgba(239,68,68,0.85)" strokeWidth="0.5" rx="3"/>
                              {[60,82,104,126,148,170,192].map(y => <rect key={`ba${y}`} x="200" y={y} width="80" height="14" fill="rgba(239,68,68,0.35)" rx="1.5"/>)}
                              <text x="240" y="222" fill="rgba(226,232,240,1.0)" fontSize="8" textAnchor="middle" fontFamily="system-ui">{t('operationalRisk.facilityHazards.retail.zones.beverage')}</text>
                              <rect x="310" y="50" width="110" height="180" fill="rgba(245,158,11,0.55)" stroke="rgba(245,158,11,0.75)" strokeWidth="0.5" rx="3"/>
                              {[60,82,104,126,148].map(y => <rect key={`sa${y}`} x="320" y={y} width="90" height="14" fill="rgba(245,158,11,0.3)" rx="1.5"/>)}
                              <text x="365" y="185" fill="rgba(226,232,240,1.0)" fontSize="8" textAnchor="middle" fontFamily="system-ui">{t('operationalRisk.facilityHazards.retail.zones.seasonal')}</text>
                              <rect x="440" y="50" width="100" height="180" fill="rgba(245,158,11,0.55)" stroke="rgba(245,158,11,0.75)" strokeWidth="0.5" rx="3"/>
                              {[60,100,140,180].map(y => <rect key={`rf${y}`} x="450" y={y} width="80" height="32" fill="rgba(245,158,11,0.3)" rx="1.5"/>)}
                              <text x="490" y="222" fill="rgba(226,232,240,1.0)" fontSize="8" textAnchor="middle" fontFamily="system-ui">{t('operationalRisk.facilityHazards.retail.zones.refrigerated')}</text>
                              <rect x="130" y="255" width="340" height="45" fill="rgba(34,197,94,0.45)" stroke="rgba(34,197,94,0.65)" strokeWidth="0.5" rx="3"/>
                              {[150,180,210,240,270,300,330,360,390,420].map(x => <rect key={`ck${x}`} x={x} y="264" width="8" height="28" fill="rgba(34,197,94,0.3)" rx="1"/>)}
                              <text x="300" y="250" fill="rgba(226,232,240,1.0)" fontSize="8" textAnchor="middle" fontFamily="system-ui">{t('operationalRisk.facilityHazards.retail.zones.checkout')}</text>
                            </svg>
                          </div>
                          <div className="lg:w-52 flex flex-col gap-4">
                            <div className="bg-slate-800/80 rounded-lg border border-slate-700 p-3">
                              <div className="space-y-2">
                                <div className="flex items-center gap-2">
                                  <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ backgroundColor: 'rgba(239,68,68,0.7)' }}/>
                                  <span className="text-[10px] text-slate-400">{t('operationalRisk.facilityHazards.legend.high')}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ backgroundColor: 'rgba(245,158,11,0.6)' }}/>
                                  <span className="text-[10px] text-slate-400">{t('operationalRisk.facilityHazards.legend.moderate')}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ backgroundColor: 'rgba(34,197,94,0.5)' }}/>
                                  <span className="text-[10px] text-slate-400">{t('operationalRisk.facilityHazards.legend.general')}</span>
                                </div>
                              </div>
                            </div>
                            <div className="border-l-2 border-[#0077FF]/30 pl-3">
                              <p className="text-[10px] text-[#4DB8FF] uppercase tracking-widest mb-1">{t('operationalRisk.facilityHazards.retail.insightLabel')}</p>
                              <p className="text-[11px] text-slate-400 leading-relaxed">{t('operationalRisk.facilityHazards.retail.insight')}</p>
                            </div>
                          </div>
                        </div>
                        <p className="text-xs text-slate-500 mt-5 text-center italic">{t('operationalRisk.facilityHazards.retail.caption')}</p>
                      </div>
                    </div>
                  </div>
                )
              },
              {
                id: 'stadium',
                label: t('operationalRisk.facilityHazards.tabs.stadium') || 'Stadium',
                content: (
                  <div data-testid="facility-stadium">
                    <div className="bg-slate-800/60 rounded-2xl border border-slate-700 overflow-hidden">
                      <div className="px-6 py-4 border-b border-slate-700 flex items-center gap-3">
                        <Trophy className="w-5 h-5 text-amber-400" />
                        <h3 className="text-lg font-bold text-white">{t('operationalRisk.facilityHazards.stadium.title')}</h3>
                      </div>
                      <div className="p-6 md:p-8">
                        <div className="flex flex-col lg:flex-row gap-8">
                          <div className="flex-1">
                            <svg viewBox="0 0 600 380" className="w-full h-auto" data-testid="stadium-diagram">
                              <defs>
                                <pattern id="stadiumGrid" width="30" height="30" patternUnits="userSpaceOnUse">
                                  <path d="M 30 0 L 0 0 0 30" fill="none" stroke="rgba(148,163,184,0.06)" strokeWidth="0.5"/>
                                </pattern>
                                <pattern id="stadiumGridFine" width="10" height="10" patternUnits="userSpaceOnUse">
                                  <path d="M 10 0 L 0 0 0 10" fill="none" stroke="rgba(148,163,184,0.025)" strokeWidth="0.3"/>
                                </pattern>
                              </defs>
                              <rect width="600" height="380" fill="#1e293b" rx="8"/>
                              <rect width="600" height="380" fill="url(#stadiumGridFine)" rx="8"/>
                              <rect width="600" height="380" fill="url(#stadiumGrid)" rx="8"/>
                              {[60,120,180,240,300,360,420,480,540].map(x => <line key={`st${x}`} x1={x} y1="0" x2={x} y2="4" stroke="rgba(148,163,184,0.12)" strokeWidth="0.5"/>)}
                              {[60,120,180,240,300,360].map(y => <line key={`sl${y}`} x1="0" y1={y} x2="4" y2={y} stroke="rgba(148,163,184,0.12)" strokeWidth="0.5"/>)}
                              <ellipse cx="300" cy="190" rx="250" ry="155" fill="none" stroke="rgba(148,163,184,0.15)" strokeWidth="0.75"/>
                              <ellipse cx="300" cy="190" rx="100" ry="60" fill="rgba(148,163,184,0.03)" stroke="rgba(148,163,184,0.1)" strokeWidth="0.5"/>
                              <text x="300" y="194" fill="rgba(148,163,184,0.8)" fontSize="9" textAnchor="middle" fontFamily="system-ui">{t('operationalRisk.facilityHazards.stadium.zones.field')}</text>
                              <ellipse cx="300" cy="190" rx="175" ry="110" fill="rgba(34,197,94,0.45)" stroke="rgba(34,197,94,0.65)" strokeWidth="0.5"/>
                              <text x="300" y="100" fill="rgba(226,232,240,1.0)" fontSize="8" textAnchor="middle" fontFamily="system-ui">{t('operationalRisk.facilityHazards.stadium.zones.seating')}</text>
                              <text x="300" y="285" fill="rgba(226,232,240,1.0)" fontSize="8" textAnchor="middle" fontFamily="system-ui">{t('operationalRisk.facilityHazards.stadium.zones.seating')}</text>
                              <ellipse cx="300" cy="190" rx="230" ry="140" fill="none" stroke="rgba(239,68,68,0.85)" strokeWidth="10"/>
                              <text x="300" y="44" fill="rgba(226,232,240,1.0)" fontSize="8" textAnchor="middle" fontFamily="system-ui">{t('operationalRisk.facilityHazards.stadium.zones.concourse')}</text>
                              <text x="300" y="345" fill="rgba(226,232,240,1.0)" fontSize="8" textAnchor="middle" fontFamily="system-ui">{t('operationalRisk.facilityHazards.stadium.zones.concourse')}</text>
                              <rect x="95" y="80" width="60" height="30" fill="rgba(239,68,68,0.65)" stroke="rgba(239,68,68,0.85)" strokeWidth="0.5" rx="3"/>
                              <text x="125" y="100" fill="rgba(226,232,240,1.0)" fontSize="7" textAnchor="middle" fontFamily="system-ui">{t('operationalRisk.facilityHazards.stadium.zones.concessions')}</text>
                              <rect x="445" y="80" width="60" height="30" fill="rgba(239,68,68,0.65)" stroke="rgba(239,68,68,0.85)" strokeWidth="0.5" rx="3"/>
                              <text x="475" y="100" fill="rgba(226,232,240,1.0)" fontSize="7" textAnchor="middle" fontFamily="system-ui">{t('operationalRisk.facilityHazards.stadium.zones.concessions')}</text>
                              <rect x="95" y="270" width="60" height="30" fill="rgba(239,68,68,0.65)" stroke="rgba(239,68,68,0.85)" strokeWidth="0.5" rx="3"/>
                              <rect x="445" y="270" width="60" height="30" fill="rgba(239,68,68,0.65)" stroke="rgba(239,68,68,0.85)" strokeWidth="0.5" rx="3"/>
                              <rect x="170" y="320" width="55" height="24" fill="rgba(245,158,11,0.55)" stroke="rgba(245,158,11,0.75)" strokeWidth="0.5" rx="2"/>
                              <text x="197" y="336" fill="rgba(226,232,240,1.0)" fontSize="7" textAnchor="middle" fontFamily="system-ui">{t('operationalRisk.facilityHazards.stadium.zones.restrooms')}</text>
                              <rect x="375" y="320" width="55" height="24" fill="rgba(245,158,11,0.55)" stroke="rgba(245,158,11,0.75)" strokeWidth="0.5" rx="2"/>
                              <text x="402" y="336" fill="rgba(226,232,240,1.0)" fontSize="7" textAnchor="middle" fontFamily="system-ui">{t('operationalRisk.facilityHazards.stadium.zones.restrooms')}</text>
                            </svg>
                          </div>
                          <div className="lg:w-52 flex flex-col gap-4">
                            <div className="bg-slate-800/80 rounded-lg border border-slate-700 p-3">
                              <div className="space-y-2">
                                <div className="flex items-center gap-2">
                                  <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ backgroundColor: 'rgba(239,68,68,0.7)' }}/>
                                  <span className="text-[10px] text-slate-400">{t('operationalRisk.facilityHazards.legend.high')}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ backgroundColor: 'rgba(245,158,11,0.6)' }}/>
                                  <span className="text-[10px] text-slate-400">{t('operationalRisk.facilityHazards.legend.moderate')}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ backgroundColor: 'rgba(34,197,94,0.5)' }}/>
                                  <span className="text-[10px] text-slate-400">{t('operationalRisk.facilityHazards.legend.general')}</span>
                                </div>
                              </div>
                            </div>
                            <div className="border-l-2 border-amber-500/30 pl-3">
                              <p className="text-[10px] text-amber-400 uppercase tracking-widest mb-1">{t('operationalRisk.facilityHazards.stadium.insightLabel')}</p>
                              <p className="text-[11px] text-slate-400 leading-relaxed">{t('operationalRisk.facilityHazards.stadium.insight')}</p>
                            </div>
                          </div>
                        </div>
                        <p className="text-xs text-slate-500 mt-5 text-center italic">{t('operationalRisk.facilityHazards.stadium.caption')}</p>
                      </div>
                    </div>
                  </div>
                )
              },
              {
                id: 'airport',
                label: t('operationalRisk.facilityHazards.tabs.airport') || 'Airport',
                content: (
                  <div data-testid="facility-airport">
                    <div className="bg-slate-800/60 rounded-2xl border border-slate-700 overflow-hidden">
                      <div className="px-6 py-4 border-b border-slate-700 flex items-center gap-3">
                        <Plane className="w-5 h-5 text-emerald-400" />
                        <h3 className="text-lg font-bold text-white">{t('operationalRisk.facilityHazards.airport.title')}</h3>
                      </div>
                      <div className="p-6 md:p-8">
                        <div className="flex flex-col lg:flex-row gap-8">
                          <div className="flex-1">
                            <svg viewBox="0 0 600 380" className="w-full h-auto" data-testid="airport-diagram">
                              <defs>
                                <pattern id="airportGrid" width="30" height="30" patternUnits="userSpaceOnUse">
                                  <path d="M 30 0 L 0 0 0 30" fill="none" stroke="rgba(148,163,184,0.06)" strokeWidth="0.5"/>
                                </pattern>
                                <pattern id="airportGridFine" width="10" height="10" patternUnits="userSpaceOnUse">
                                  <path d="M 10 0 L 0 0 0 10" fill="none" stroke="rgba(148,163,184,0.025)" strokeWidth="0.3"/>
                                </pattern>
                              </defs>
                              <rect width="600" height="380" fill="#1e293b" rx="8"/>
                              <rect width="600" height="380" fill="url(#airportGridFine)" rx="8"/>
                              <rect width="600" height="380" fill="url(#airportGrid)" rx="8"/>
                              {[60,120,180,240,300,360,420,480,540].map(x => <line key={`at${x}`} x1={x} y1="0" x2={x} y2="4" stroke="rgba(148,163,184,0.12)" strokeWidth="0.5"/>)}
                              {[60,120,180,240,300,360].map(y => <line key={`al${y}`} x1="0" y1={y} x2="4" y2={y} stroke="rgba(148,163,184,0.12)" strokeWidth="0.5"/>)}
                              <rect x="40" y="140" width="520" height="100" fill="rgba(148,163,184,0.03)" stroke="rgba(148,163,184,0.12)" strokeWidth="0.5" rx="4"/>
                              <rect x="55" y="165" width="490" height="50" fill="rgba(245,158,11,0.55)" stroke="rgba(245,158,11,0.75)" strokeWidth="0.5" rx="2"/>
                              <text x="300" y="195" fill="rgba(226,232,240,1.0)" fontSize="8" textAnchor="middle" fontFamily="system-ui">{t('operationalRisk.facilityHazards.airport.zones.walkways')}</text>
                              <rect x="55" y="142" width="120" height="20" fill="rgba(34,197,94,0.45)" stroke="rgba(34,197,94,0.65)" strokeWidth="0.5" rx="2"/>
                              <text x="115" y="156" fill="rgba(226,232,240,1.0)" fontSize="7" textAnchor="middle" fontFamily="system-ui">{t('operationalRisk.facilityHazards.airport.zones.security')}</text>
                              <rect x="40" y="30" width="160" height="100" fill="rgba(148,163,184,0.02)" stroke="rgba(148,163,184,0.1)" strokeWidth="0.5" rx="3"/>
                              {[50,90,130,170].map(x => <rect key={`gal${x}`} x={x} y="40" width="30" height="18" fill="rgba(245,158,11,0.55)" stroke="rgba(245,158,11,0.75)" strokeWidth="0.5" rx="1.5"/>)}
                              <text x="120" y="75" fill="rgba(226,232,240,1.0)" fontSize="7" textAnchor="middle" fontFamily="system-ui">{t('operationalRisk.facilityHazards.airport.zones.gates')}</text>
                              <rect x="400" y="30" width="160" height="100" fill="rgba(148,163,184,0.02)" stroke="rgba(148,163,184,0.1)" strokeWidth="0.5" rx="3"/>
                              {[400,440,480,520].map(x => <rect key={`gar${x}`} x={x} y="40" width="30" height="18" fill="rgba(245,158,11,0.55)" stroke="rgba(245,158,11,0.75)" strokeWidth="0.5" rx="1.5"/>)}
                              <text x="480" y="75" fill="rgba(226,232,240,1.0)" fontSize="7" textAnchor="middle" fontFamily="system-ui">{t('operationalRisk.facilityHazards.airport.zones.gates')}</text>
                              <rect x="230" y="250" width="140" height="65" fill="rgba(239,68,68,0.65)" stroke="rgba(239,68,68,0.85)" strokeWidth="0.5" rx="3"/>
                              {[[245,260],[280,260],[315,260],[245,286],[280,286],[315,286]].map(([x,y],i) => <rect key={`fc${i}`} x={x} y={y} width="25" height="18" fill="rgba(239,68,68,0.35)" rx="1.5"/>)}
                              <text x="300" y="327" fill="rgba(226,232,240,1.0)" fontSize="8" textAnchor="middle" fontFamily="system-ui">{t('operationalRisk.facilityHazards.airport.zones.foodcourt')}</text>
                              <rect x="80" y="250" width="60" height="35" fill="rgba(239,68,68,0.65)" stroke="rgba(239,68,68,0.85)" strokeWidth="0.5" rx="2"/>
                              <text x="110" y="272" fill="rgba(226,232,240,1.0)" fontSize="7" textAnchor="middle" fontFamily="system-ui">{t('operationalRisk.facilityHazards.airport.zones.restrooms')}</text>
                              <rect x="460" y="250" width="60" height="35" fill="rgba(239,68,68,0.65)" stroke="rgba(239,68,68,0.85)" strokeWidth="0.5" rx="2"/>
                              <text x="490" y="272" fill="rgba(226,232,240,1.0)" fontSize="7" textAnchor="middle" fontFamily="system-ui">{t('operationalRisk.facilityHazards.airport.zones.restrooms')}</text>
                              <rect x="100" y="130" width="40" height="12" fill="rgba(148,163,184,0.04)" stroke="rgba(148,163,184,0.08)" strokeWidth="0.5" rx="1"/>
                              <rect x="460" y="130" width="40" height="12" fill="rgba(148,163,184,0.04)" stroke="rgba(148,163,184,0.08)" strokeWidth="0.5" rx="1"/>
                            </svg>
                          </div>
                          <div className="lg:w-52 flex flex-col gap-4">
                            <div className="bg-slate-800/80 rounded-lg border border-slate-700 p-3">
                              <div className="space-y-2">
                                <div className="flex items-center gap-2">
                                  <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ backgroundColor: 'rgba(239,68,68,0.7)' }}/>
                                  <span className="text-[10px] text-slate-400">{t('operationalRisk.facilityHazards.legend.high')}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ backgroundColor: 'rgba(245,158,11,0.6)' }}/>
                                  <span className="text-[10px] text-slate-400">{t('operationalRisk.facilityHazards.legend.moderate')}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ backgroundColor: 'rgba(34,197,94,0.5)' }}/>
                                  <span className="text-[10px] text-slate-400">{t('operationalRisk.facilityHazards.legend.general')}</span>
                                </div>
                              </div>
                            </div>
                            <div className="border-l-2 border-emerald-500/30 pl-3">
                              <p className="text-[10px] text-emerald-400 uppercase tracking-widest mb-1">{t('operationalRisk.facilityHazards.airport.insightLabel')}</p>
                              <p className="text-[11px] text-slate-400 leading-relaxed">{t('operationalRisk.facilityHazards.airport.insight')}</p>
                            </div>
                          </div>
                        </div>
                        <p className="text-xs text-slate-500 mt-5 text-center italic">{t('operationalRisk.facilityHazards.airport.caption')}</p>
                      </div>
                    </div>
                  </div>
                )
              }
            ]}
          />

          {/* ── INSIGHT PANEL ── */}
          <div className="max-w-3xl mx-auto mt-14" data-testid="facility-insight-panel">
            <div className="bg-slate-800/80 rounded-2xl border border-slate-700 p-8 text-center">
              <h3 className="text-xl md:text-2xl font-bold text-white mb-4">{t('operationalRisk.facilityHazards.insightPanel.title')}</h3>
              <p className="text-slate-400 leading-relaxed mb-2">{t('operationalRisk.facilityHazards.insightPanel.body1')}</p>
              <p className="text-slate-400 leading-relaxed">{t('operationalRisk.facilityHazards.insightPanel.body2')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════ 6. HEADLINE INSIGHT + PROBABILITY MODELS ═══════════ */}
      <section className="py-20 bg-slate-900" data-testid="probability-models-section">
        <div className="container-max px-6 md:px-12 lg:px-24">
          {/* Headline Insight — always visible */}
          <div className="text-center max-w-4xl mx-auto mb-8" data-testid="headline-insight">
            <span className="text-caption text-[#4DB8FF] mb-4 block">{t('operationalRisk.probabilityModels.label')}</span>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              {t('operationalRisk.headlineInsight.title')}
            </h2>
            <p className="text-lg text-slate-400 max-w-3xl mx-auto">
              {t('operationalRisk.headlineInsight.subtitle')}
            </p>
          </div>

          <AccordionSection
            testId="probability-models-accordion"
            expandLabel={t('operationalRisk.accordion.showModels')}
            collapseLabel={t('operationalRisk.accordion.hideModels')}
            visibleContent={
              <div className="text-center">
                <p className="text-sm text-slate-500 max-w-2xl mx-auto">
                  {t('operationalRisk.probabilityModels.comparisonContext')}
                </p>
              </div>
            }
            expandableContent={
              <div className="mt-8 space-y-10">
                {/* ── RETAIL MODEL ── */}
                <div data-testid="model-retail">
                  <div className="bg-slate-800/60 rounded-2xl border border-slate-700 overflow-hidden">
                    <div className="px-6 py-4 border-b border-slate-700 flex items-center gap-3">
                      <ShoppingCart className="w-5 h-5 text-[#4DB8FF]" />
                      <h3 className="text-lg font-bold text-white">{t('operationalRisk.probabilityModels.retail.title')}</h3>
                      <span className="ml-auto text-xs font-medium text-slate-500 bg-slate-700/60 px-3 py-1 rounded-full">{t('operationalRisk.probabilityModels.retail.environment')}</span>
                    </div>
                    <div className="grid lg:grid-cols-3 divide-y lg:divide-y-0 lg:divide-x divide-slate-700">
                      <div className="p-6">
                        <p className="text-sm text-slate-400 leading-relaxed">{t('operationalRisk.probabilityModels.retail.description')}</p>
                        {t('operationalRisk.probabilityModels.retail.note') && (
                          <p className="text-xs text-slate-500 italic mt-4 border-t border-slate-700 pt-4">{t('operationalRisk.probabilityModels.retail.note')}</p>
                        )}
                        <p className="text-[10px] text-slate-600 mt-2">{t('operationalRisk.probabilityModels.modelDisclaimer')}</p>
                      </div>
                      <div className="p-6">
                        <div className="grid grid-cols-2 gap-6">
                          <StatPanel label={t('operationalRisk.probabilityModels.annualIncidents')} value={t('operationalRisk.probabilityModels.retail.annualIncidents')} />
                          <StatPanel label={t('operationalRisk.probabilityModels.escalationRate')} value={t('operationalRisk.probabilityModels.retail.escalationRate')} />
                          <StatPanel label={t('operationalRisk.probabilityModels.annualClaims')} value={t('operationalRisk.probabilityModels.retail.annualClaims')} />
                          <StatPanel label={t('operationalRisk.probabilityModels.estimatedExposure')} value={t('operationalRisk.probabilityModels.retail.estimatedExposure')} />
                        </div>
                      </div>
                      <div className="p-6">
                        <p className="text-[10px] text-slate-500 uppercase tracking-widest mb-4">{t('operationalRisk.probabilityModels.comparisonTitle')}</p>
                        <ComparisonChart data={comparisonData} maxValue={10} accentColor="sky" contextLine={t('operationalRisk.probabilityModels.comparisonContext')} attributionLine={t('operationalRisk.probabilityModels.chartAttribution')} />
                      </div>
                    </div>
                  </div>
                </div>

                {/* ── STADIUM MODEL ── */}
                <div data-testid="model-stadium">
                  <div className="bg-slate-800/60 rounded-2xl border border-slate-700 overflow-hidden">
                    <div className="px-6 py-4 border-b border-slate-700 flex items-center gap-3">
                      <Trophy className="w-5 h-5 text-amber-400" />
                      <h3 className="text-lg font-bold text-white">{t('operationalRisk.probabilityModels.stadium.title')}</h3>
                      <span className="ml-auto text-xs font-medium text-slate-500 bg-slate-700/60 px-3 py-1 rounded-full">{t('operationalRisk.probabilityModels.stadium.environment')}</span>
                    </div>
                    <div className="grid lg:grid-cols-3 divide-y lg:divide-y-0 lg:divide-x divide-slate-700">
                      <div className="p-6">
                        <p className="text-sm text-slate-400 leading-relaxed mb-4">{t('operationalRisk.probabilityModels.stadium.description')}</p>
                        {(t('operationalRisk.probabilityModels.stadium.commonHazards') || []).length > 0 && (
                          <ul className="space-y-1.5">
                            {t('operationalRisk.probabilityModels.stadium.commonHazards').map((h, i) => (
                              <li key={i} className="flex items-center gap-2 text-xs text-slate-500">
                                <span className="w-1 h-1 bg-amber-500 rounded-full flex-shrink-0" />
                                {h}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                      <div className="p-6">
                        <div className="grid grid-cols-2 gap-6">
                          <StatPanel label={t('operationalRisk.probabilityModels.annualIncidents')} value={t('operationalRisk.probabilityModels.stadium.annualIncidents')} />
                          <StatPanel label={t('operationalRisk.probabilityModels.escalationRate')} value={t('operationalRisk.probabilityModels.stadium.escalationRate')} />
                          <StatPanel label={t('operationalRisk.probabilityModels.annualClaims')} value={t('operationalRisk.probabilityModels.stadium.annualClaims')} />
                          <StatPanel label={t('operationalRisk.probabilityModels.estimatedExposure')} value={t('operationalRisk.probabilityModels.stadium.estimatedExposure')} />
                        </div>
                      </div>
                      <div className="p-6">
                        <p className="text-[10px] text-slate-500 uppercase tracking-widest mb-4">{t('operationalRisk.probabilityModels.comparisonTitle')}</p>
                        <ComparisonChart data={comparisonData} maxValue={10} accentColor="amber" contextLine={t('operationalRisk.probabilityModels.comparisonContext')} attributionLine={t('operationalRisk.probabilityModels.chartAttribution')} />
                      </div>
                    </div>
                  </div>
                </div>

                {/* ── AIRPORT MODEL ── */}
                <div data-testid="model-airport">
                  <div className="bg-slate-800/60 rounded-2xl border border-slate-700 overflow-hidden">
                    <div className="px-6 py-4 border-b border-slate-700 flex items-center gap-3">
                      <Plane className="w-5 h-5 text-emerald-400" />
                      <h3 className="text-lg font-bold text-white">{t('operationalRisk.probabilityModels.airport.title')}</h3>
                      <span className="ml-auto text-xs font-medium text-slate-500 bg-slate-700/60 px-3 py-1 rounded-full">{t('operationalRisk.probabilityModels.airport.environment')}</span>
                    </div>
                    <div className="grid lg:grid-cols-3 divide-y lg:divide-y-0 lg:divide-x divide-slate-700">
                      <div className="p-6">
                        <p className="text-sm text-slate-400 leading-relaxed mb-4">{t('operationalRisk.probabilityModels.airport.description')}</p>
                        {(t('operationalRisk.probabilityModels.airport.commonHazards') || []).length > 0 && (
                          <ul className="space-y-1.5">
                            {t('operationalRisk.probabilityModels.airport.commonHazards').map((h, i) => (
                              <li key={i} className="flex items-center gap-2 text-xs text-slate-500">
                                <span className="w-1 h-1 bg-emerald-500 rounded-full flex-shrink-0" />
                                {h}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                      <div className="p-6">
                        <div className="grid grid-cols-2 gap-6">
                          <StatPanel label={t('operationalRisk.probabilityModels.annualIncidents')} value={t('operationalRisk.probabilityModels.airport.annualIncidents')} />
                          <StatPanel label={t('operationalRisk.probabilityModels.escalationRate')} value={t('operationalRisk.probabilityModels.airport.escalationRate')} />
                          <StatPanel label={t('operationalRisk.probabilityModels.annualClaims')} value={t('operationalRisk.probabilityModels.airport.annualClaims')} />
                          <StatPanel label={t('operationalRisk.probabilityModels.estimatedExposure')} value={t('operationalRisk.probabilityModels.airport.estimatedExposure')} />
                        </div>
                      </div>
                      <div className="p-6">
                        <p className="text-[10px] text-slate-500 uppercase tracking-widest mb-4">{t('operationalRisk.probabilityModels.comparisonTitle')}</p>
                        <ComparisonChart data={comparisonData} maxValue={10} accentColor="emerald" contextLine={t('operationalRisk.probabilityModels.comparisonContext')} attributionLine={t('operationalRisk.probabilityModels.chartAttribution')} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            }
          />
        </div>
      </section>

      {/* Reinforcement: After Models */}
      <div className="py-8 bg-slate-900" data-testid="reinforcement-models">
        <div className="container-max px-6 md:px-12 lg:px-24">
          <div className="max-w-3xl mx-auto bg-[#0077FF]/10 rounded-xl border border-[#0077FF]/20 p-6 flex items-start gap-4">
            <div className="w-8 h-8 bg-[#0077FF]/20 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
              <Shield className="w-4 h-4 text-[#4DB8FF]" />
            </div>
            <div>
              <h4 className="text-sm font-bold text-white mb-1">{t('operationalRisk.reinforcement.afterModels.title')}</h4>
              <p className="text-sm text-slate-400 leading-relaxed">{t('operationalRisk.reinforcement.afterModels.body')}</p>
            </div>
          </div>
        </div>
      </div>

      {/* ═══════════ 7. OPERATIONAL RISK INSIGHT ═══════════ */}
      <section className="py-20 bg-slate-800" data-testid="risk-insight-section">
        <div className="container-max px-6 md:px-12 lg:px-24">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <span className="text-caption text-[#4DB8FF] mb-4 block">{t('operationalRisk.riskInsight.label')}</span>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">{t('operationalRisk.riskInsight.title')}</h2>
            <p className="text-lg text-slate-400">{t('operationalRisk.riskInsight.subtitle')}</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto mb-10">
            <FlowDiagram
              title={t('operationalRisk.riskInsight.traditional.title')}
              steps={t('operationalRisk.riskInsight.traditional.steps') || []}
              color="red"
              icon={XCircle}
            />
            <FlowDiagram
              title={t('operationalRisk.riskInsight.tapelligence.title')}
              steps={t('operationalRisk.riskInsight.tapelligence.steps') || []}
              color="sky"
              icon={CheckCircle}
            />
          </div>

          <div className="text-center">
            <p className="text-lg text-slate-300 font-medium max-w-2xl mx-auto">{t('operationalRisk.riskInsight.conclusion')}</p>
          </div>
        </div>
      </section>

      {/* ═══════════ 8. VISIBILITY GAP — DETAILED COMPARISON ═══════════ */}
      <section className="section-padding bg-slate-50" data-testid="visibility-gap-section">
        <div className="container-max px-6 md:px-12 lg:px-24">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <span className="text-caption text-[#0077FF] mb-4 block">{t('operationalRisk.visibilityGap.label')}</span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">{t('operationalRisk.visibilityGap.title')}</h2>
            <p className="text-lg text-slate-600">{t('operationalRisk.visibilityGap.subtitle')}</p>
          </div>
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* Traditional */}
            <div className="bg-white rounded-2xl p-8 border border-red-200 shadow-sm" data-testid="traditional-flow">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                  <XCircle className="w-5 h-5 text-red-500" />
                </div>
                <h3 className="text-lg font-bold text-slate-900">{t('operationalRisk.visibilityGap.traditional.title')}</h3>
              </div>
              <div className="space-y-0 mb-6">
                {(t('operationalRisk.visibilityGap.traditional.steps') || []).map((step, i, arr) => (
                  <div key={i}>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-red-50 border border-red-200 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-red-500 text-xs font-bold">{i + 1}</span>
                      </div>
                      <span className="text-slate-700 font-medium text-sm">{step}</span>
                    </div>
                    {i < arr.length - 1 && <div className="ml-4 border-l-2 border-red-200 h-5" />}
                  </div>
                ))}
              </div>
              <div className="bg-red-50 rounded-xl p-4 border border-red-100">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wider">{t('operationalRisk.visibilityGap.comparison.traditional.timeLabel')}</p>
                    <p className="text-lg font-bold text-red-600">{t('operationalRisk.visibilityGap.comparison.traditional.time')}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-slate-500 uppercase tracking-wider">{t('operationalRisk.visibilityGap.comparison.traditional.costLabel')}</p>
                    <p className="text-lg font-bold text-red-600">{t('operationalRisk.visibilityGap.comparison.traditional.cost')}</p>
                  </div>
                </div>
              </div>
            </div>
            {/* Tapelligence */}
            <div className="bg-white rounded-2xl p-8 border border-[#0077FF]/20 shadow-sm" data-testid="tapelligence-flow">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-[#0077FF]/10 rounded-lg flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-[#0077FF]" />
                </div>
                <h3 className="text-lg font-bold text-slate-900">{t('operationalRisk.visibilityGap.tapelligence.title')}</h3>
              </div>
              <div className="space-y-0 mb-6">
                {(t('operationalRisk.visibilityGap.tapelligence.steps') || []).map((step, i, arr) => (
                  <div key={i}>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-[#0077FF]/5 border border-[#0077FF]/20 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-[#0077FF] text-xs font-bold">{i + 1}</span>
                      </div>
                      <span className="text-slate-700 font-medium text-sm">{step}</span>
                    </div>
                    {i < arr.length - 1 && <div className="ml-4 border-l-2 border-[#0077FF]/20 h-5" />}
                  </div>
                ))}
              </div>
              <div className="bg-[#0077FF]/5 rounded-xl p-4 border border-[#0077FF]/10">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wider">{t('operationalRisk.visibilityGap.comparison.tapelligence.timeLabel')}</p>
                    <p className="text-lg font-bold text-[#0077FF]">{t('operationalRisk.visibilityGap.comparison.tapelligence.time')}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-slate-500 uppercase tracking-wider">{t('operationalRisk.visibilityGap.comparison.tapelligence.costLabel')}</p>
                    <p className="text-lg font-bold text-emerald-600">{t('operationalRisk.visibilityGap.comparison.tapelligence.cost')}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════ 9. TAPELLIGENCE INTELLIGENCE LAYER ═══════════ */}
      <section className="section-padding bg-white" data-testid="intelligence-layer-section">
        <div className="container-max px-6 md:px-12 lg:px-24">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <span className="text-caption text-[#0077FF] mb-4 block">{t('operationalRisk.intelligenceLayer.label')}</span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">{t('operationalRisk.intelligenceLayer.title')}</h2>
            <p className="text-lg text-slate-600">{t('operationalRisk.intelligenceLayer.subtitle')}</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto mb-12">
            {(() => {
              const capIcons = [Zap, Activity, Eye, Shield];
              const capColors = [
                { bg: 'bg-[#0077FF]/5', iconBg: 'bg-[#0077FF]/10', text: 'text-[#0077FF]', border: 'border-[#0077FF]/20' },
                { bg: 'bg-amber-50', iconBg: 'bg-amber-100', text: 'text-amber-600', border: 'border-amber-200' },
                { bg: 'bg-emerald-50', iconBg: 'bg-emerald-100', text: 'text-emerald-600', border: 'border-emerald-200' },
                { bg: 'bg-violet-50', iconBg: 'bg-violet-100', text: 'text-violet-600', border: 'border-violet-200' },
              ];
              return (t('operationalRisk.intelligenceLayer.capabilities') || []).map((cap, i) => {
                const Ic = capIcons[i]; const c = capColors[i];
                return (
                  <div key={i} className={`${c.bg} rounded-2xl p-6 border ${c.border}`} data-testid={`capability-card-${i}`}>
                    <div className={`w-12 h-12 ${c.iconBg} rounded-xl flex items-center justify-center mb-4`}>
                      <Ic className={`w-6 h-6 ${c.text}`} />
                    </div>
                    <h3 className="text-base font-bold text-slate-900 mb-2">{cap.title}</h3>
                    <p className="text-sm text-slate-600 leading-relaxed">{cap.description}</p>
                  </div>
                );
              });
            })()}
          </div>
          <div className="text-center">
            <p className="text-lg font-semibold text-slate-800 max-w-2xl mx-auto">{t('operationalRisk.intelligenceLayer.conclusion')}</p>
          </div>
        </div>
      </section>

      {/* ═══════════ 10. DEPLOYMENT ESTIMATOR ═══════════ */}
      <section ref={calculatorRef} className="section-padding bg-slate-50 scroll-mt-28" id="estimator-section" data-testid="deployment-estimator-section">
        <div className="container-max px-6 md:px-12 lg:px-24">
          <DeploymentEstimator language={language} />
        </div>
      </section>

      {/* ═══════════ 10b. EXPOSURE & LOSS CALCULATOR ═══════════ */}
      <section className="section-padding bg-white scroll-mt-28" id="exposure" data-testid="exposure-calculator-section">
        <div className="container-max px-6 md:px-12 lg:px-24">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <span className="text-caption text-[#0077FF] mb-4 block">Financial Exposure</span>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" data-testid="exposure-title">Exposure &amp; Loss Calculator</h2>
              <p className="text-lg text-slate-600">Estimate your organization's annual hazard exposure based on facility characteristics.</p>
            </div>
            <div className="bg-white rounded-2xl border border-slate-200 shadow-lg overflow-hidden">
              <div className="bg-[#FF7F00] px-8 py-5 flex items-center gap-3 rounded-t-2xl">
                <DollarSign className="w-5 h-5 text-white" />
                <h3 className="text-white font-semibold">Exposure &amp; Loss Calculator</h3>
              </div>
              <div className="p-8 md:p-10">
                <div className="grid md:grid-cols-2 gap-6 mb-8">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Industry Type</label>
                    <select
                      value={calcIndustry}
                      onChange={e => setCalcIndustry(e.target.value)}
                      className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#0077FF]"
                      data-testid="exposure-calc-industry"
                    >
                      <option value="">Select industry</option>
                      <option value="Retail Warehouse">Retail Warehouse</option>
                      <option value="Grocery Store">Grocery Store</option>
                      <option value="Airport">Airport</option>
                      <option value="Stadium">Stadium</option>
                      <option value="Hotel / Hospitality">Hotel / Hospitality</option>
                      <option value="Healthcare Facility">Healthcare Facility</option>
                      <option value="Restaurant Chain">Restaurant Chain</option>
                      <option value="Shopping Mall">Shopping Mall</option>
                      <option value="Casino / Gaming">Casino / Gaming</option>
                      <option value="Real Estate / Property">Real Estate / Property</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Facility Size (sq ft)</label>
                    <input
                      type="number"
                      value={calcSize}
                      onChange={e => setCalcSize(e.target.value)}
                      placeholder="50,000"
                      className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#0077FF]"
                      data-testid="exposure-calc-size"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Average Daily Visitors</label>
                    <input
                      type="number"
                      value={calcVisitors}
                      onChange={e => setCalcVisitors(e.target.value)}
                      placeholder="500"
                      className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#0077FF]"
                      data-testid="exposure-calc-visitors"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Number of Locations</label>
                    <input
                      type="number"
                      value={calcLocations}
                      onChange={e => setCalcLocations(e.target.value)}
                      placeholder="1"
                      className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#0077FF]"
                      data-testid="exposure-calc-locations"
                    />
                  </div>
                </div>
                <button
                  onClick={calculateExposure}
                  className="w-full md:w-auto px-8 py-3.5 bg-[#FF7F00] hover:bg-[#E67200] text-white font-semibold rounded-xl transition-colors flex items-center justify-center gap-2"
                  data-testid="exposure-calculate-btn"
                >
                  <Calculator className="w-5 h-5" />
                  Calculate Exposure
                </button>

                {calcResults && (
                  <div className="mt-8 grid md:grid-cols-3 gap-5" data-testid="exposure-calc-results">
                    <div className="bg-white rounded-xl p-6 border border-red-200 text-center">
                      <AlertTriangle className="w-8 h-8 text-red-500 mx-auto mb-3" />
                      <p className="text-3xl font-bold text-slate-900 mb-1">{calcResults.incidents.toLocaleString()}</p>
                      <p className="text-sm text-slate-600">Estimated Incidents / Year</p>
                    </div>
                    <div className="bg-white rounded-xl p-6 border border-amber-200 text-center">
                      <DollarSign className="w-8 h-8 text-amber-500 mx-auto mb-3" />
                      <p className="text-3xl font-bold text-slate-900 mb-1">${calcResults.liability.toLocaleString()}</p>
                      <p className="text-sm text-slate-600">Estimated Liability Exposure</p>
                    </div>
                    <div className="bg-white rounded-xl p-6 border border-orange-200 text-center">
                      <Clock className="w-8 h-8 text-[#FF7F00] mx-auto mb-3" />
                      <p className="text-3xl font-bold text-slate-900 mb-1">${calcResults.disruption.toLocaleString()}</p>
                      <p className="text-sm text-slate-600">Estimated Operational Disruption</p>
                    </div>
                  </div>
                )}
                <p className="text-xs text-slate-400 mt-6">Estimates based on industry averages and published research. Actual exposure may vary based on operational conditions, safety protocols, and facility management practices.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════ 11. RESEARCH REPORT DOWNLOAD ═══════════ */}
      <section className="section-padding bg-white" data-testid="research-download-section">
        <div className="container-max px-6 md:px-12 lg:px-24">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <span className="text-caption text-[#0077FF] mb-4 block">Research</span>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" data-testid="research-title">Operational Risk Research &amp; Data</h2>
              <p className="text-lg text-slate-600 max-w-2xl mx-auto">Industry-validated analysis of incident frequency, detection delays, and liability exposure across large physical environments.</p>
            </div>
            <div className="grid md:grid-cols-2 gap-8 items-start">
              <div className="bg-slate-50 rounded-2xl border border-slate-200 p-6">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                    <FileText className="w-5 h-5 text-red-600" />
                  </div>
                  <h3 className="text-base font-bold text-slate-900">What's Inside</h3>
                </div>
                <ul className="space-y-3">
                  {[
                    'Executive summary with key statistical findings',
                    'Incident frequency data by environment type',
                    'Detection delay and claim escalation analysis',
                    'Financial impact modeling with multi-location projections',
                    'Environment-based risk profiles (retail, transit, hospitality)',
                    'Operational visibility gap assessment',
                    'Full source citations (NSC, BLS, CDC, NFSI, III)',
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-sm text-slate-600">
                      <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="flex flex-col items-center justify-center text-center">
                <div className="w-20 h-20 bg-[#FF7F00]/10 rounded-2xl flex items-center justify-center mb-6">
                  <FileText className="w-10 h-10 text-[#FF7F00]" />
                </div>
                <p className="text-sm font-semibold text-slate-900 mb-1">Tapelligence Operational Risk Report</p>
                <p className="text-xs text-slate-500 mb-6">PDF — 10+ pages — March 2026</p>
                <a
                  href="/research/tapelligence-operational-risk-report.pdf"
                  download
                  className="inline-flex items-center gap-2 px-8 py-3.5 bg-[#FF7F00] hover:bg-[#E67200] text-white font-semibold rounded-xl transition-colors shadow-sm"
                  data-testid="research-download-btn"
                >
                  <Download className="w-5 h-5" />
                  Download Full Report
                </a>
                <p className="text-xs text-slate-400 mt-4 max-w-sm leading-relaxed">No login required</p>
                <p className="text-[11px] text-slate-400 mt-2 max-w-sm leading-relaxed italic">Based on aggregated research from NSC, BLS, CDC, NFSI, and insurance industry data</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════ 12. SOURCES ═══════════ */}
      <section className="section-padding bg-slate-50" data-testid="sources-section">
        <div className="container-max px-6 md:px-12 lg:px-24">
          <div className="max-w-4xl mx-auto">
            <AccordionSection
              testId="sources-accordion"
              expandLabel={t('operationalRisk.accordion.showSources')}
              collapseLabel={t('operationalRisk.accordion.hideSources')}
              visibleContent={
                <>
                  <div className="text-center mb-10">
                    <span className="text-caption text-slate-500 mb-4 block">{t('operationalRisk.sources.label')}</span>
                    <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-3">{t('operationalRisk.sources.title')}</h2>
                    <p className="text-base text-slate-600">{t('operationalRisk.sources.subtitle')}</p>
                  </div>
                  {/* Numbered Citations — always visible */}
                  <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="citations-list">
                    <h4 className="text-sm font-semibold text-slate-800 mb-4">{t('operationalRisk.sources.citationsTitle')}</h4>
                    <div className="space-y-2">
                      {(t('operationalRisk.sources.citations') || []).map((cite, i) => (
                        <p key={i} className="text-xs text-slate-600 leading-relaxed">{cite}</p>
                      ))}
                    </div>
                  </div>
                </>
              }
              expandableContent={
                <div className="mt-8">
                  {/* Research Organization Logos */}
                  <div className="bg-white rounded-xl border border-slate-200 p-6 mb-8 text-center" data-testid="research-logos">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-5">{t('operationalRisk.sources.logosTitle')}</p>
                    <div className="flex items-center justify-center gap-8 md:gap-12 flex-wrap">
                      <div className="flex flex-col items-center gap-1.5 opacity-60 hover:opacity-90 transition-opacity">
                        <svg viewBox="0 0 120 40" className="h-8 w-auto"><text x="60" y="24" textAnchor="middle" fill="#334155" fontSize="16" fontWeight="700" fontFamily="system-ui">NSC</text><text x="60" y="36" textAnchor="middle" fill="#64748b" fontSize="6" fontFamily="system-ui">National Safety Council</text></svg>
                      </div>
                      <div className="flex flex-col items-center gap-1.5 opacity-60 hover:opacity-90 transition-opacity">
                        <svg viewBox="0 0 120 40" className="h-8 w-auto"><text x="60" y="24" textAnchor="middle" fill="#334155" fontSize="16" fontWeight="700" fontFamily="system-ui">OSHA</text><text x="60" y="36" textAnchor="middle" fill="#64748b" fontSize="6" fontFamily="system-ui">U.S. Dept. of Labor</text></svg>
                      </div>
                      <div className="flex flex-col items-center gap-1.5 opacity-60 hover:opacity-90 transition-opacity">
                        <svg viewBox="0 0 120 40" className="h-8 w-auto"><text x="60" y="24" textAnchor="middle" fill="#334155" fontSize="16" fontWeight="700" fontFamily="system-ui">BLS</text><text x="60" y="36" textAnchor="middle" fill="#64748b" fontSize="6" fontFamily="system-ui">Bureau of Labor Statistics</text></svg>
                      </div>
                      <div className="flex flex-col items-center gap-1.5 opacity-60 hover:opacity-90 transition-opacity">
                        <svg viewBox="0 0 120 40" className="h-8 w-auto"><text x="60" y="24" textAnchor="middle" fill="#334155" fontSize="16" fontWeight="700" fontFamily="system-ui">NFSI</text><text x="60" y="36" textAnchor="middle" fill="#64748b" fontSize="6" fontFamily="system-ui">Natl. Floor Safety Institute</text></svg>
                      </div>
                    </div>
                  </div>

                  <p className="text-sm text-slate-600 mb-4">{t('operationalRisk.sources.researchIntro')}</p>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {(t('operationalRisk.sources.items') || []).map((src, i) => (
                      <a
                        key={i}
                        href={src.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bg-white rounded-xl border border-slate-200 p-5 hover:border-slate-300 hover:shadow-sm transition-all group"
                        data-testid={`source-${i}`}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <h4 className="text-sm font-semibold text-slate-800 group-hover:text-[#0077FF] transition-colors leading-snug pr-2">{src.name}</h4>
                          <ExternalLink className="w-3.5 h-3.5 text-slate-400 group-hover:text-[#0077FF] flex-shrink-0 mt-0.5" />
                        </div>
                        <p className="text-xs text-slate-500">{src.context}</p>
                      </a>
                    ))}
                  </div>
                </div>
              }
            />
          </div>
        </div>
      </section>

      {/* ═══════════ 13. CTA ═══════════ */}
      <section className="py-20 bg-slate-900" data-testid="risk-cta-section">
        <div className="container-max px-6 md:px-12 lg:px-24 text-center">
          <p className="text-2xl md:text-3xl lg:text-4xl font-bold text-white leading-tight max-w-4xl mx-auto mb-4">
            {t('operationalRisk.cta.title')}
          </p>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto mb-10">
            {t('operationalRisk.cta.subtitle')}
          </p>
          <Link to={getLocalizedPath('/contact')} className="btn-primary bg-white text-slate-900 hover:bg-slate-100 text-base" data-testid="risk-cta-btn">
            {t('operationalRisk.cta.btn')}
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>

    </div>
  );
};

export default OperationalRiskPage;
